/**
 * Sign in — ELF-CIRCLE.
 *
 * The shipped template put a stock mountain photograph and a placeholder "H"
 * badge in front of anyone signing in, which is the first screen an admin sees
 * and the only one a visitor reaches from the footer. This is the same design
 * language as the rest of the site: a film still under a scrim on the left,
 * the form on `--deep` on the right.
 *
 * `--deep` is the one palette token that stays dark in every family, which is
 * why the header uses it too — so the panel's white-on-dark treatment holds in
 * all nine palettes and in both modes without a second set of colours.
 *
 * Only the presentation changed. The auth calls, error handling and redirect
 * behaviour are the template's.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuth } from '../hooks';
import { ElfAuthShell } from '../components';
import { route } from '../../../shared/utils';

const LoginPage: React.FC = () => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  useEffect(() => {
    if (isAuthenticated) setLoginError(null);
  }, [isAuthenticated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSubmitting) return false;

    setLoginError(null);
    setIsSubmitting(true);
    try {
      const result = await login({
        email: formData.email,
        password: formData.password,
        rememberMe: formData.rememberMe,
      });
      if (!result.success) setLoginError('Invalid username or password');
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : 'Network error occurred');
    } finally {
      setIsSubmitting(false);
    }
    return false;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // Only the very first auth check, not the login round-trip.
  const checkingSession = isLoading && !loginError && !formData.email && !formData.password;

  return (
    <>
      <SEO
        title="Sign in"
        description="Sign in to the ELF-CIRCLE desk."
        keywords="login, sign in, elf-circle"
      />

      <ElfAuthShell
        kicker="Sign in"
        title="Welcome back"
        lede="Use the address the account was created with."
      >
            {checkingSession ? (
              <div className="elf-auth__checking">
                <Loader2 className="elf-auth__spin" aria-hidden="true" />
                <span>Checking your session…</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="elf-auth__form" noValidate>
                {loginError && (
                  <div className="elf-auth__err" role="alert">
                    {loginError}
                  </div>
                )}

                <label className="elf-auth__field">
                  <span>Email</span>
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@company.com"
                  />
                </label>

                <label className="elf-auth__field">
                  <span>Password</span>
                  <span className="elf-auth__pwwrap">
                    <input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="elf-auth__peek"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                    </button>
                  </span>
                </label>

                <div className="elf-auth__row">
                  <label className="elf-auth__check">
                    <input
                      name="rememberMe"
                      type="checkbox"
                      checked={formData.rememberMe}
                      onChange={handleInputChange}
                    />
                    <span>Keep me signed in</span>
                  </label>
                  <Link className="elf-auth__link" to={route('/auth/forgot-password')}>
                    Forgot password?
                  </Link>
                </div>

                <button type="submit" className="pillcta elf-auth__submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Signing in…' : 'Sign in'}
                  <span aria-hidden="true">
                    {isSubmitting ? <Loader2 className="elf-auth__spin" /> : <ArrowRight />}
                  </span>
                </button>

                <p className="elf-auth__alt">
                  No account yet?{' '}
                  <Link className="elf-auth__link" to={route('/auth/register')}>
                    Request access
                  </Link>
                </p>
              </form>
        )}
      </ElfAuthShell>
    </>
  );
};

export default LoginPage;
